import React, { useState } from 'react';
import { CheckCircle, Send } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';
import emailjs from '@emailjs/browser';

// EmailJS configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'contact@exevori.com';

export default function DevisPage() {
  const { lang } = useLang();
  const tr = t[lang];
  const dTr = tr.devisPage;
  const [ref, visible] = useScrollReveal();
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    sector: '', need: '', solution_type: '', features: '',
    budget: '', deadline: '', message: ''
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      // Format du message complet
      const fullMessage = `
=== CLIENT ===
Nom: ${form.name}
Entreprise: ${form.company || 'Non spécifié'}
Email: ${form.email}
Téléphone: ${form.phone || 'Non spécifié'}
Secteur: ${form.sector || 'Non spécifié'}

=== PROJET ===
Besoin: ${form.need || 'Non spécifié'}
Type de solution: ${form.solution_type || 'Non spécifié'}
Fonctionnalités: ${form.features || 'Non spécifié'}
Budget: ${form.budget || 'Non spécifié'}
Délai: ${form.deadline || 'Non spécifié'}

=== MESSAGE ===
${form.message || 'Aucun message'}
      `.trim();

      // Paramètres EmailJS avec les variables exactes du template
      const templateParams = {
        title: `[Devis] Nouvelle demande de ${form.name}`,
        name: form.name,
        email: form.email,
        time: new Date().toLocaleString('fr-CA'),
        message: fullMessage
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('EmailJS Success:', response);
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', sector: '', need: '', solution_type: '', features: '', budget: '', deadline: '', message: '' });
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      setStatus('error');
    }
  };

  return (
    <main data-testid="devis-page">
      {/* Hero */}
      <section className="pt-32 pb-16 relative" style={{ backgroundColor: '#0D1F1F' }}>
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(155,89,245,0.4) 0%, transparent 50%)' }}
        />
        <div className="section-container relative text-center max-w-3xl mx-auto">
          <h1 className="font-sora font-black text-4xl sm:text-5xl text-white mb-3 animate-fadeInUp">
            {dTr.hero.title}
          </h1>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto mb-6" />
          <p className="text-gray-300 text-base md:text-lg font-inter animate-fadeInUp delay-100">
            {dTr.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div ref={ref} className={`max-w-3xl mx-auto reveal ${visible ? 'visible' : ''}`}>
            {status === 'success' ? (
              <div className="exevori-card p-16 text-center">
                <div
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                >
                  <CheckCircle size={36} className="text-white" />
                </div>
                <h2 className="font-sora font-bold text-2xl text-white mb-4">
                  {lang === 'fr' ? 'Demande envoyée !' : 'Request Sent!'}
                </h2>
                <p className="text-gray-300 font-inter max-w-md mx-auto">{dTr.form.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="devis-form">
                {/* Section 1: Personal */}
                <div className="exevori-card p-8 mb-6">
                  <h2 className="font-sora font-bold text-lg text-white mb-6 flex items-center gap-2">
                    <span
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-black"
                      style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                    >1</span>
                    {dTr.form.personal}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="exevori-label">{dTr.form.name} *</label>
                      <input
                        name="name" value={form.name} onChange={handleChange} required
                        className="exevori-input" data-testid="devis-name"
                        placeholder={lang === 'fr' ? 'Jean Tremblay' : 'John Smith'}
                      />
                    </div>
                    <div>
                      <label className="exevori-label">{dTr.form.company}</label>
                      <input
                        name="company" value={form.company} onChange={handleChange}
                        className="exevori-input" data-testid="devis-company"
                        placeholder={lang === 'fr' ? 'Mon Entreprise Inc.' : 'My Company Inc.'}
                      />
                    </div>
                    <div>
                      <label className="exevori-label">{dTr.form.email} *</label>
                      <input
                        type="email" name="email" value={form.email} onChange={handleChange} required
                        className="exevori-input" data-testid="devis-email"
                        placeholder="jean@entreprise.ca"
                      />
                    </div>
                    <div>
                      <label className="exevori-label">{dTr.form.phone}</label>
                      <input
                        name="phone" value={form.phone} onChange={handleChange}
                        className="exevori-input" data-testid="devis-phone"
                        placeholder="581-700-4171"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="exevori-label">{dTr.form.sector}</label>
                      <select name="sector" value={form.sector} onChange={handleChange} className="exevori-select" data-testid="devis-sector">
                        <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                        {dTr.form.sectors.map((s, i) => <option key={i} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Project */}
                <div className="exevori-card p-8 mb-6">
                  <h2 className="font-sora font-bold text-lg text-white mb-6 flex items-center gap-2">
                    <span
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-black"
                      style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                    >2</span>
                    {dTr.form.project}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="exevori-label">{dTr.form.need}</label>
                      <select name="need" value={form.need} onChange={handleChange} className="exevori-select" data-testid="devis-need">
                        <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                        {dTr.form.needs.map((n, i) => <option key={i} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="exevori-label">{dTr.form.solution}</label>
                      <select name="solution_type" value={form.solution_type} onChange={handleChange} className="exevori-select" data-testid="devis-solution">
                        <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                        {dTr.form.solutions.map((s, i) => <option key={i} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="exevori-label">{dTr.form.budget}</label>
                      <select name="budget" value={form.budget} onChange={handleChange} className="exevori-select" data-testid="devis-budget">
                        <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                        {dTr.form.budgets.map((b, i) => <option key={i} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="exevori-label">{dTr.form.deadline}</label>
                      <select name="deadline" value={form.deadline} onChange={handleChange} className="exevori-select" data-testid="devis-deadline">
                        <option value="">— {lang === 'fr' ? 'Sélectionner' : 'Select'} —</option>
                        {dTr.form.deadlines.map((d, i) => <option key={i} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="exevori-label">{dTr.form.features}</label>
                      <input
                        name="features" value={form.features} onChange={handleChange}
                        className="exevori-input" data-testid="devis-features"
                        placeholder={dTr.form.featuresPlaceholder}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="exevori-label">{dTr.form.message}</label>
                      <textarea
                        name="message" value={form.message} onChange={handleChange}
                        rows={6} className="exevori-input resize-none" data-testid="devis-message"
                        placeholder={dTr.form.messagePlaceholder}
                      />
                    </div>
                  </div>
                </div>

                {status === 'error' && (
                  <p className="text-red-400 text-sm mb-4 font-inter text-center">{dTr.form.error}</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  data-testid="devis-submit"
                  className="btn-primary w-full justify-center text-base py-4"
                >
                  <Send size={18} />
                  {status === 'sending' ? dTr.form.sending : dTr.form.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
